import {
  Box,
  createStyles,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IndeterminateCheckBoxOutlinedIcon from "@material-ui/icons/IndeterminateCheckBoxOutlined";
import TreeItem from "@material-ui/lab/TreeItem";
import TreeView from "@material-ui/lab/TreeView";
import { useAuth } from "auth";
import newANPGLogo from "Assets/Images/NewANPGLogo.png";
import ElementSizes from "Constants/ElementSizes";
import Routes from "Constants/Routes";
import { Roles } from "Enums/Roles";
import { Block } from "Models/Block";
import { RouteParams } from "Models/RouteParams";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import uuid from "uuid";
import { ArchivedAuditIcon } from "Views/Components/Icons/ArchivedAuditIcon";
import { CurrentAuditIcon } from "Views/Components/Icons/CurrentAuditIcon";
import { SiteDocumentsIcon } from "Views/Components/Icons/SiteDocumentsIcon";
import { CarDetailsIcon } from "../Icons/CarDetailsIcon";
import axios from "axios";

interface Props {
  readonly blocks: Block[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      width: ElementSizes.DRAWER_WIDTH,
    },
    profileSection: {
      justifyContent: "space-between",
      display: "flex",
      paddingBottom: theme.spacing(2),
    },
    navigationSection: {
      justifyContent: "space-between",
      display: "flex",
      padding: theme.spacing(2),
      color: "#ffffff",
      textAlign: "center",
      paddingLeft: theme.spacing(6),
    },
    navigationBox: {
      flexGrow: 1,
      width: ElementSizes.DRAWER_WIDTH,
    },
    treeViewSection: {
      paddingLeft: theme.spacing(2),
    },
    treeBlockItemSection: {
      paddingTop: theme.spacing(0.625),
    },
    image: {
      height: ElementSizes.LOGO_HEIGHT,
      marginLeft: "auto",
      marginRight: "auto",
      width: "100%",
      display: "block",
    },
    archiveIcon: {
      width: 40,
      height: 50,
    },
    currentIcon: {
      width: 40,
      height: 50,
      paddingLeft: theme.spacing(0.5),
    },
    siteDocumentsIcon: {
      width: 40,
      height: 50,
    },
    detailsIcon: {
      width: 40,
      height: 50,
    },
    labelRoot: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0.5, 0),
    },
    labelText: {
      fontWeight: "inherit",
      flexGrow: 1,
    },
  }),
);

export const BlockTreeView: FC<Props> = ({ blocks }) => {
  const classes = useStyles();
  const { t } = useTranslation("blocks");
  const { auth } = useAuth();

  const fpsoId = useRouteMatch<RouteParams>(Routes.FPSO)?.params.fpsoId;
  const blockId = useRouteMatch<RouteParams>(Routes.BLOCK)?.params.blockId;
  const fpsoAreaId = useRouteMatch<RouteParams>(Routes.FPSO_AREA)?.params
    .fpsoAreaId;

  const location = useLocation();
  const history = useHistory();

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [reportMetaData, setReportMetaData] = useState<any[]>([]);

  const initializeExpandedNodes = useCallback(
    (
      currentFpsoId: string | undefined,
      currentBlockId: string | undefined,
      currentFpsoAreaId: string | undefined,
      currentPath: string,
    ) => {
      const initializedNodes: string[] = [];

      let pathSplit = currentPath.split("/");
      const pathSuffix = pathSplit.pop();
      const pathPrefix = pathSplit.pop();

      if (
        pathSuffix &&
        [Routes.GRAPHICAL_RESULTS_BASEPATH, Routes.KPI_TABLE_BASEPATH].includes(
          pathSuffix,
        )
      ) {
        setExpandedNodes([
          "hydrocarbonExportAuditing",
          Routes.MANAGEMENT_SUMMARY_BASEPATH,
        ]);
        return;
      }

      const isCPOPath =
        currentPath.includes(Routes.CPO_FILE_SUBMISSION_BASEPATH) ||
        currentPath.includes(Routes.CPO_DEFERRAL_BASEPATH) ||
        currentPath.includes(Routes.CPO_PRODUCTION_VALUES_BASEPATH) ||
        currentPath.includes(Routes.CPO_FILE_PROCESSING_BASEPATH);

      if (isCPOPath) {
        initializedNodes.push("cpo");
        if (currentBlockId) {
          initializedNodes.push("cpo" + currentBlockId);
        }
        setExpandedNodes(initializedNodes);
        return;
      }

      if (pathPrefix && Routes.CPO_BUSINESS_INTELLIGENCE.includes(pathPrefix)) {
        setExpandedNodes([Routes.BI_REPORT]);
        return;
      }

      if (currentFpsoId || currentBlockId || currentFpsoAreaId) {
        initializedNodes.push("hydrocarbonExportAuditing");

        if (currentBlockId) {
          initializedNodes.push(currentBlockId);
        }

        if (currentFpsoId) {
          const blockIdByFpsoId = blocks.find((block) =>
            block.fpsos.find((fpso) => fpso.id === currentFpsoId),
          )?.id;
          if (blockIdByFpsoId) {
            initializedNodes.push(blockIdByFpsoId);
          }
          initializedNodes.push(currentFpsoId);
        }

        if (currentFpsoAreaId) {
          const blockIdByFpsoId = blocks.find((block) =>
            block.fpsos.find((fpso) =>
              fpso.fpsoAreas.find(
                (fpsoArea) => fpsoArea.id === currentFpsoAreaId,
              ),
            ),
          )?.id;
          const fpsoIdByBlockId = blocks
            .find((block) => block.id === blockIdByFpsoId)
            ?.fpsos.find((fpso) =>
              fpso.fpsoAreas.find(
                (fpsoArea) => fpsoArea.id === currentFpsoAreaId,
              ),
            )?.id;

          if (blockIdByFpsoId && fpsoIdByBlockId) {
            initializedNodes.push(
              blockIdByFpsoId,
              fpsoIdByBlockId,
              currentFpsoAreaId,
            );
          }
        }
        setExpandedNodes(initializedNodes);
        return;
      }
      setExpandedNodes([]);
    },
    [blocks],
  );

  const initializeSelectedNode = useCallback(
    (currentPath: string) => {
      const pathSuffix = currentPath.split("/").pop();
      if (pathSuffix === Routes.CPO_FILE_SUBMISSION_BASEPATH) {
        setSelectedNode(`${blockId}_${Routes.CPO_FILE_SUBMISSION_BASEPATH}`);
      } else if (pathSuffix === Routes.CPO_DEFERRAL_BASEPATH) {
        setSelectedNode(`${blockId}_${Routes.CPO_DEFERRAL_BASEPATH}`);
      } else if (pathSuffix === Routes.CPO_PRODUCTION_VALUES_BASEPATH) {
        setSelectedNode(`${blockId}_${Routes.CPO_PRODUCTION_VALUES_BASEPATH}`);
      } else if (pathSuffix === Routes.CPO_FILE_PROCESSING_BASEPATH) {
      } else if (pathSuffix === fpsoId) {
        setSelectedNode(fpsoId ? fpsoId : "");
      } else if (pathSuffix === blockId) {
        setSelectedNode(blockId ? blockId : "");
      } else if (pathSuffix === fpsoAreaId) {
        setSelectedNode(fpsoAreaId ? fpsoAreaId : "");
      } else if (blockId) {
        setSelectedNode(`${blockId}_${pathSuffix}`);
      } else if (fpsoId) {
        setSelectedNode(`${fpsoId}_${pathSuffix}`);
      } else if (fpsoAreaId) {
        setSelectedNode(`${fpsoAreaId}_${pathSuffix}`);
      } else {
        setSelectedNode(pathSuffix ? pathSuffix : "");
      }
    },
    [fpsoAreaId, fpsoId, blockId],
  );

  const [selectedNode, setSelectedNode] = useState<string>("");

  useEffect(() => {
    initializeExpandedNodes(fpsoId, blockId, fpsoAreaId, location.pathname);
    initializeSelectedNode(location.pathname);
  }, [
    location.pathname,
    fpsoAreaId,
    fpsoId,
    blockId,
    initializeExpandedNodes,
    initializeSelectedNode,
  ]);

  const getReports = useCallback(async () => {
    const token = await auth?.getToken();
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    axios
      .get(`${process.env.REACT_APP_FILE_SERVER_URL}/powerbi/reports`, config)
      .then((response: any) => {
        setReportMetaData(response?.data);
      })
      .catch((error: any) => {
        console.warn(error);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getReports();
  }, [getReports]);

  const handleNodeToggle = (event: React.ChangeEvent<{}>, nodes: string[]) => {
    const isCollapsingAll = expandedNodes.every(
      (node) => !nodes.includes(node),
    );
    if (isCollapsingAll) {
      setExpandedNodes(nodes);
      return;
    }

    const hydrocarbonExportAuditingId = "hydrocarbonExportAuditing";
    //const managementSummaryId = Routes.MANAGEMENT_SUMMARY_BASEPATH;
    const cpoId = "cpo";

    const toggledNode = nodes.find((node) => !expandedNodes.includes(node));

    const isTogglingHydrocarbon = toggledNode === hydrocarbonExportAuditingId;
    //const isTogglingManagement = toggledNode === managementSummaryId;
    const isTogglingCPO = toggledNode === cpoId;

    if (isTogglingHydrocarbon) {
      if (nodes.includes(hydrocarbonExportAuditingId)) {
        setExpandedNodes([hydrocarbonExportAuditingId]);
      } else {
        setExpandedNodes([]);
      }
      // } else if (isTogglingManagement) {
      //   if (nodes.includes(managementSummaryId)) {
      //     setExpandedNodes([managementSummaryId]);
      //   } else {
      //     setExpandedNodes([]);
      //   }
    } else if (isTogglingCPO) {
      if (nodes.includes(cpoId)) {
        setExpandedNodes([cpoId]);
      } else {
        setExpandedNodes([]);
      }
    } else {
      setExpandedNodes(nodes);
    }
  };

  const handleNodeSelect = (event: React.ChangeEvent<{}>, nodeId: string) => {
    setSelectedNode(nodeId);
  };

  const handleBlockClick = (selectedBlockId: string) => {
    window.scrollTo(0, 0);
    history.push(`/block/${selectedBlockId}`);
  };

  const handleFpsoClick = (selectedFpsoId: string) => {
    window.scrollTo(0, 0);
    history.push(`/fpso/${selectedFpsoId}`);
  };

  const handleFpsoAreaClick = (selectedFpsoAreaId: string) => {
    window.scrollTo(0, 0);
    history.push(`/fpsoArea/${selectedFpsoAreaId}`);
  };

  const handleSystemDetailsClick = (selectedFpsoAreaId: string) => {
    window.scrollTo(0, 0);
    history.push(
      `/fpsoArea/${selectedFpsoAreaId}/${Routes.SYSTEM_DETAILS_BASEPATH}`,
    );
  };

  const handleCurrentAuditClick = (selectedFpsoAreaId: string) => {
    window.scrollTo(0, 0);
    if (selectedFpsoAreaId) {
      history.push(
        `/fpsoArea/${selectedFpsoAreaId}/${Routes.CURRENT_AUDIT_BASEPATH}`,
      );
    } else {
      history.push(`/fpsoArea/${uuid()}/${Routes.CURRENT_AUDIT_BASEPATH}`);
    }
  };

  const handleArchivedAuditsClick = (selectedFpsoAreaId: string) => {
    window.scrollTo(0, 0);
    history.push(
      `/fpsoArea/${selectedFpsoAreaId}/${Routes.ARCHIVED_AUDITS_BASEPATH}`,
    );
  };

  const handleFpsoDocumentsClick = (selectedFpsoAreaId: string) => {
    window.scrollTo(0, 0);
    history.push(
      `/fpsoArea/${selectedFpsoAreaId}/${Routes.DOCUMENTS_BASEPATH}`,
    );
  };

  const handleCPOFileSubmissionClick = (selectedBlockId: string) => {
    window.scrollTo(0, 0);
    history.push(
      `/block/${selectedBlockId}/${Routes.CPO_FILE_SUBMISSION_BASEPATH}`,
    );
  };

  const handleCPODeferralClick = (selectedBlockId: string) => {
    window.scrollTo(0, 0);
    history.push(`/block/${selectedBlockId}/${Routes.CPO_DEFERRAL_BASEPATH}`);
  };

  const handleCPODailyProductionValuesClick = (selectedBlockId: string) => {
    window.scrollTo(0, 0);
    history.push(
      `/block/${selectedBlockId}/${Routes.CPO_PRODUCTION_VALUES_BASEPATH}`,
    );
  };

  const handleCollapseAllClick = () => {
    setExpandedNodes([]);
  };

  const handleGraphicalResultsClick = () => {
    window.scrollTo(0, 0);
    history.push(Routes.GRAPHICAL_RESULTS);
  };

  const handleKpiTableClick = () => {
    window.scrollTo(0, 0);
    history.push(Routes.KPI_TABLE);
  };

  const handleUserFeedbackClick = () => {
    window.scrollTo(0, 0);
    history.push(Routes.USER_FEEDBACK);
  };

  const handleBiReportClick = (tag: string) => {
    window.scrollTo(0, 0);
    history.push(`/bi/${tag}`);
  };

  return (
    <div className={classes.root}>
      <Tooltip title={t<string>("menu:anpgWebsite")} arrow={true}>
        <a href="https://anpg.co.ao/" target="_blank" rel="noopener noreferrer">
          <img className={classes.image} src={newANPGLogo} alt="ANPG Logo" />
        </a>
      </Tooltip>
      <Box bgcolor="primary.main">
        <Typography variant="h6" className={classes.navigationSection}>
          {t("title")}
          <IconButton size="small" onClick={() => handleCollapseAllClick()}>
            <IndeterminateCheckBoxOutlinedIcon style={{ color: "#ffffff" }} />
          </IconButton>
        </Typography>
      </Box>
      <TreeView
        expanded={expandedNodes}
        selected={selectedNode}
        onNodeToggle={handleNodeToggle}
        onNodeSelect={handleNodeSelect}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        className={classes.treeViewSection}
      >
        {!auth?.hasRealmRole(Roles.anpgManagement) && (
        <TreeItem
          nodeId={"hydrocarbonExportAuditing"}
          key={"hydrocarbonExportAuditing"}
          label={t("hydrocarbonExportAuditing")}
        >
          {blocks.map((block) => (
            <TreeItem
              nodeId={block.id}
              key={block.id}
              label={t("block") + " " + block.name}
              onClick={() => handleBlockClick(block.id)}
              className={classes.treeBlockItemSection}
            >
              {block.fpsos.map((fpso) => (
                <TreeItem
                  nodeId={fpso.id}
                  label={fpso.name}
                  key={fpso.id}
                  onClick={() => handleFpsoClick(fpso.id)}
                  className={classes.treeBlockItemSection}
                >
                  {fpso.fpsoAreas.map((fpsoArea) => (
                    <>
                      {(fpsoArea.areaName === "Hydrocarbon Export" ||
                        fpsoArea.areaName === "Management") && (
                        <TreeItem
                          nodeId={fpsoArea.id}
                          label={fpsoArea.areaName}
                          key={fpsoArea.id}
                          onClick={() => handleFpsoAreaClick(fpsoArea.id)}
                          className={classes.treeBlockItemSection}
                        >
                          {fpsoArea.areaName !== "Management" ? (
                            <TreeItem
                              nodeId={`${fpsoArea.id}_${Routes.SYSTEM_DETAILS_BASEPATH}`}
                              key={`${fpsoArea.id}_${Routes.SYSTEM_DETAILS_BASEPATH}`}
                              label={
                                <div className={classes.labelRoot}>
                                  <div className={classes.detailsIcon}>
                                    <CarDetailsIcon />
                                  </div>
                                  <Typography
                                    variant="body2"
                                    className={classes.labelText}
                                  >
                                    {t("systemDetails")}
                                  </Typography>
                                </div>
                              }
                              onClick={() =>
                                handleSystemDetailsClick(fpsoArea.id)
                              }
                            />
                          ) : (
                            <></>
                          )}
                          <TreeItem
                            nodeId={`${fpsoArea.id}_${Routes.CURRENT_AUDIT_BASEPATH}`}
                            key={`${fpsoArea.id}_${Routes.CURRENT_AUDIT_BASEPATH}`}
                            label={
                              <div className={classes.labelRoot}>
                                <div className={classes.currentIcon}>
                                  <CurrentAuditIcon />
                                </div>
                                <Typography
                                  variant="body2"
                                  className={classes.labelText}
                                >
                                  {t("currentAudit")}
                                </Typography>
                              </div>
                            }
                            onClick={() => handleCurrentAuditClick(fpsoArea.id)}
                          />
                          <TreeItem
                            nodeId={`${fpsoArea.id}_${Routes.ARCHIVED_AUDITS_BASEPATH}`}
                            key={`${fpsoArea.id}_${Routes.ARCHIVED_AUDITS_BASEPATH}`}
                            label={
                              <div className={classes.labelRoot}>
                                <div className={classes.archiveIcon}>
                                  <ArchivedAuditIcon />
                                </div>
                                <Typography
                                  variant="body2"
                                  className={classes.labelText}
                                >
                                  {t("archivedAudits")}
                                </Typography>
                              </div>
                            }
                            onClick={() =>
                              handleArchivedAuditsClick(fpsoArea.id)
                            }
                          />
                          <TreeItem
                            nodeId={`${fpsoArea.id}_${Routes.DOCUMENTS_BASEPATH}`}
                            key={`${fpsoArea.id}_${Routes.DOCUMENTS_BASEPATH}`}
                            label={
                              <div className={classes.labelRoot}>
                                <div className={classes.siteDocumentsIcon}>
                                  <SiteDocumentsIcon />
                                </div>
                                <Typography
                                  variant="body2"
                                  className={classes.labelText}
                                >
                                  {t("areaDocuments")}
                                </Typography>
                              </div>
                            }
                            onClick={() =>
                              handleFpsoDocumentsClick(fpsoArea.id)
                            }
                          />
                        </TreeItem>
                      )}
                    </>
                  ))}
                </TreeItem>
              ))}
            </TreeItem>
          ))}
          {(auth?.hasRealmRole(Roles.auditor) ||
            auth?.hasRealmRole(Roles.administrator) ||
            auth?.hasRealmRole(Roles.agency) ||
            auth?.hasRealmRole(Roles.agency_v2)) && (
            <TreeItem
              nodeId={Routes.MANAGEMENT_SUMMARY_BASEPATH}
              key={Routes.MANAGEMENT_SUMMARY_BASEPATH}
              label={t("managementSummary")}
              className={classes.treeBlockItemSection}
            >
              <TreeItem
                nodeId={Routes.KPI_TABLE_BASEPATH}
                key={Routes.KPI_TABLE_BASEPATH}
                label={t("actualAuditStatus")}
                onClick={() => handleKpiTableClick()}
              />
              <TreeItem
                nodeId={Routes.GRAPHICAL_RESULTS_BASEPATH}
                key={Routes.GRAPHICAL_RESULTS_BASEPATH}
                label={t("graphicalResults")}
                onClick={() => handleGraphicalResultsClick()}
              />
            </TreeItem>
          )}
        </TreeItem>
          )}
        {!(auth?.hasRealmRole(Roles.operator) 
        ||
          auth?.hasRealmRole(Roles.anpgManagement)
        ) && (
          <TreeItem nodeId={"cpo"} key={"cpo"} label={"CPO"}>
            {blocks.map((block) => (
              <TreeItem
                nodeId={"cpo" + block.id}
                key={"cpo" + block.id}
                label={t("block") + " " + block.name}
                className={classes.treeBlockItemSection}
              >
                {(auth?.hasRealmRole(Roles.cpoFileSubmitter) ||
                  auth?.hasRealmRole(Roles.administrator) ||
                  auth?.hasRealmRole(Roles.cpoFileSubmissionViewer)) && (
                  <TreeItem
                    nodeId={`${block.id}_${Routes.CPO_FILE_SUBMISSION_BASEPATH}`}
                    key={`${block.id}_${Routes.CPO_FILE_SUBMISSION_BASEPATH}`}
                    label={t("fileSubmission")}
                    onClick={() => handleCPOFileSubmissionClick(block.id)}
                    className={classes.treeBlockItemSection}
                  ></TreeItem>
                )}
                {(auth?.hasRealmRole(Roles.cpoDeferralEditor) ||
                  auth?.hasRealmRole(Roles.administrator)) && (
                  <TreeItem
                    nodeId={`${block.id}_${Routes.CPO_DEFERRAL_BASEPATH}`}
                    key={`${block.id}_${Routes.CPO_DEFERRAL_BASEPATH}`}
                    label={t("CPODeferral")}
                    onClick={() => handleCPODeferralClick(block.id)}
                    className={classes.treeBlockItemSection}
                  ></TreeItem>
                )}
                {(auth?.hasRealmRole(
                  Roles.cpoDailyProductionValuesEditor,
                ) ||
                  auth?.hasRealmRole(Roles.administrator)) && (
                  <TreeItem
                    nodeId={`${block.id}_${Routes.CPO_PRODUCTION_VALUES_BASEPATH}`}
                    key={`${block.id}_${Routes.CPO_PRODUCTION_VALUES_BASEPATH}`}
                    label={t("CPODailyProductionValues")}
                    onClick={() =>
                      handleCPODailyProductionValuesClick(block.id)
                    }
                    className={classes.treeBlockItemSection}
                  ></TreeItem>
                )}
              </TreeItem>
            ))}
          </TreeItem>
        )}

        {(auth?.hasRealmRole(Roles.biUser)  ||
                  auth?.hasRealmRole(Roles.administrator)) &&
          (reportMetaData?.length > 0 ? (
            <TreeItem
              nodeId={Routes.BI_REPORT}
              key={Routes.BI_REPORT}
              label={t("anpgBiReport")}
              className={classes.treeBlockItemSection}
            >
              {reportMetaData.filter((x) => auth?.hasRealmRole(Roles.anpgManagement)
              ? x.reportTitle === "ANPG Board Summary"
              || x.reportTitle === "ANPG Management Test"
              || x.reportTitle === "ANPG Management"
            : true
          ).map((x) => (
                <TreeItem
                  nodeId={Routes.BI_REPORT + x.tag}
                  key={Routes.BI_REPORT + x.tag}
                  label={x.reportTitle === "ANPG Dev Report" ? 
                    t("anpgDevReport") 
                    : x.reportTitle === "ANPG Preliminary Report" ?
                    t("anpgPreliminaryReport") 
                    : x.reportTitle === "ANPG Facility Test" ?
                    t("anpgFacilityTest")
                    : x.reportTitle === "ANPG CPO Report" ?
                    t("anpgCpoReport")
                    : x.reportTitle === "ANPG Diagnostics Test" ?
                    t("anpgDiagnosticsTest")
                    : x.reportTitle === "ANPG Board Summary" ?
                    t("anpgBoardSummary")
                    : x.reportTitle === "ANPG Management Test" ? 
                    t("anpgManagementTest")
                    : x.reportTitle === "ANPG Contracts Demo" ?
                    t("anpgContractsDemo")
                    : x.reportTitle}
                  onClick={() => handleBiReportClick(x.tag)}
                />
              ))}
            </TreeItem>
          ) : (
            <TreeItem
              nodeId={Routes.BI_REPORT}
              key={Routes.BI_REPORT}
              label={t("anpgBiReport")}
              onClick={() => handleBiReportClick("anpg")}
            />
          ))}
        <TreeItem
          nodeId={Routes.USER_FEEDBACK_BASEPATH}
          key={Routes.USER_FEEDBACK_BASEPATH}
          label={t("userFeedback")}
          className={classes.treeBlockItemSection}
          onClick={() => handleUserFeedbackClick()}
        />
      </TreeView>
    </div>
  );
};
